import type { ReactNode } from 'react';
import { Massager } from '@/types/admin-clinic-detail';
import { convGtJDate } from '@/utils/dateConverter';
import { type TableProps } from 'antd';
import { Link } from 'react-router-dom';
import { AppButton, AppCheckbox } from '../atoms';
import { EditFill, Plus } from '../atoms/AppIcon';
import AppTable from '../atoms/AppTable';

interface ClinicMassagerTableProps {
  data: Massager[];
  clinicId: number;
  siteType: string;
  showOutsidePeriod?: boolean;
  onShowOutsidePeriodChange?: (checked: boolean) => void;
  loading?: boolean;
}

const AVAILABLE_TO_INDEFINITE = '2100-12-31';

const renderExpiryPeriod = (
  availableFrom: string | null | undefined,
  availableTo: string | null | undefined
): ReactNode => {
  const fromStr = convGtJDate(availableFrom ?? '');
  if (!fromStr) return '';

  const normalizedTo =
    availableTo && availableTo.slice(0, 10) === AVAILABLE_TO_INDEFINITE ? '' : (availableTo ?? '');
  const toStr = convGtJDate(normalizedTo);
  if (toStr) {
    return (
      <>
        {fromStr}から
        <br />
        {toStr}
      </>
    );
  }

  return `${fromStr}から`;
};

const formatCertificate = (record: Massager): string => {
  const labels: string[] = [];
  if (record.acupuncture_license_number) labels.push('はり師');
  if (record.moxibustion_license_number) labels.push('きゅう師');
  if (record.massage_license_number) labels.push('マッサージ師');
  return labels.join('、');
};

const ClinicMassagerTable = ({
  data,
  clinicId,
  siteType,
  showOutsidePeriod,
  onShowOutsidePeriodChange,
  loading,
}: ClinicMassagerTableProps) => {
  const columns: TableProps<Massager>['columns'] = [
    {
      title: '施術者番号',
      dataIndex: 'serial_number',
      key: 'serial_number',
      width: '10%',
    },
    {
      title: '施術者名',
      dataIndex: 'name',
      key: 'name',
      width: '180px',
    },
    {
      title: '資格',
      key: 'certificate',
      width: '190px',
      render: (_, record) => formatCertificate(record),
    },
    {
      title: '有効期限（はり・きゅう）',
      key: 'date_a2',
      render: (_, record) => (
        <p>{renderExpiryPeriod(record.available_from_a2, record.available_to_a2)}</p>
      ),
      width: '100px',
    },
    {
      title: '有効期限（マッサージ）',
      key: 'date_r2',
      render: (_, record) => (
        <p>{renderExpiryPeriod(record.available_from_r2, record.available_to_r2)}</p>
      ),
      width: '100px',
    },
    {
      title: '操作',
      key: 'action',
      width: '140px',
      render: (_, record) => (
        <div className="flex gap-2">
          <Link to={`/${siteType}/admin/massagers/${record.id}/edit`}>
            <AppButton icon={<EditFill />} type="primary">
              編集
            </AppButton>
          </Link>
        </div>
      ),
    },
  ];
  return (
    <section>
      <div className="flex items-center gap-10 mb-4">
        <h3 className="text-xl">施術者</h3>
        <Link to={`/${siteType}/admin/massagers/create?clinic_id=${clinicId}`}>
          <AppButton icon={<Plus />} type="primary">
            新規
          </AppButton>
        </Link>
      </div>
      {onShowOutsidePeriodChange && (
        <div className="mb-3 flex justify-end">
          <AppCheckbox
            checked={!!showOutsidePeriod}
            onChange={e => onShowOutsidePeriodChange(e.target.checked)}
          >
            有効期限外を表示する
          </AppCheckbox>
        </div>
      )}
      <AppTable isAdminSite columns={columns} dataSource={data} rowKey={record => record.id} loading={loading} />
    </section>
  );
};

export default ClinicMassagerTable;
